import { createContext, FC, useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'components/CurrentUserProvider';
import { makeAfter } from 'lib';

type PaginationContextType = {
  page: number;
  itemsPerPage: number;
  variables: {
    first: number;
    after?: string;
  };
  changePage: (page: number) => void;
};

type Props = {
  navigate?: boolean;
};

const PaginationContext = createContext<PaginationContextType>({
  page: 0,
  itemsPerPage: 50,
  variables: { first: 50 },
  changePage: () => {},
});

export const usePagination = () => useContext(PaginationContext);

const Pagination: FC<Props> = ({ children, navigate = true }) => {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(Number(params.get('page') || 0));
  const { itemsPerPage } = useCurrentUser();

  const variables = {
    first: itemsPerPage,
    after: makeAfter(page, itemsPerPage),
  };

  const changePage = useCallback(
    (page: number) => {
      setPage(page);

      if (navigate) {
        params.set('page', page.toString());
        setParams(params, { replace: true });
      }
    },
    [navigate, params, setParams]
  );

  return (
    <PaginationContext.Provider
      value={{ page, itemsPerPage, variables, changePage }}>
      {children}
    </PaginationContext.Provider>
  );
};

export default Pagination;
